import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TableHeadComponent } from "../../../common/TableHead";
import { integrationsMatcher } from "../../../utils/integrationsMatcher";
import {
  Autocomplete,
  TextField,
  Switch,
  Tooltip,
  TableHead,
  Typography,
} from "@mui/material";
import {
  SiteInfo,
  PatchObject,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { checkedCircleIcon } from "../../../common/checkedCircleIcon";
import { cancelIconRed } from "../../../common/cancelIconRed";
import { vmsBuilder } from "./SiteAboutPageUtils/vmsBuilder";
import { useAppStore } from "../../../../appStore";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { safeGetItem } from "../../../../utils/localStorageUtils";
import { getVmsDisplayLabel } from "./SiteAboutPageUtils/getVmsDisplayLabel";
import { visibleVmsVariables } from "./SiteAboutPageUtils/visibleVmsVariables";
import { NatWireguardConflictDialog } from "./NatWireguardConflictDialog";
import { useNatWireguardConflictDialog } from "./SiteAboutPageUtils/useNatWireguardConflictDialog";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

interface GroupOption {
  id: number;
  name: string;
  parent: number;
  parent_account: boolean;
}

interface AlarmOption {
  name: string;
  value: string | number;
}

export const GeneralInfoTabEditMode = ({
  setPatchObject,
  setSiteInfo,
  siteInfo,
  setSiteInfoChangesMade,
  vmsVariables,
  setVmsVariables,
}: {
  setPatchObject: Dispatcher<PatchObject>;
  setSiteInfo: Dispatcher<SiteInfo>;
  siteInfo: SiteInfo;
  setSiteInfoChangesMade: Dispatcher<boolean>;
  vmsVariables: any;
  setVmsVariables: any;
}) => {
  const { userPermissions, wireGuardOptions } = useAppStore();
  const { isFeatureEnabled } = useFeatureFlag();
  const showWireguard = isFeatureEnabled("showWireguard", false);
  // Only WireGuard-enabled sites (display_wireguard) show the AZ selector.
  // ONVIF-over-WireGuard sites qualify because save_site enables the WireGuard
  // AdminFeature for them on onboard.
  const shouldShowWireguardAvailabilityZone =
    showWireguard && Boolean(siteInfo.display_wireguard);

  const {
    ecsNatInstance,
    wireguard: wireguardId,
    wireguardAutocompleteKey,
    handleWireguardSelection,
    handleNatToggle,
    conflictDialogProps,
  } = useNatWireguardConflictDialog({
    siteInfo,
    showWireguardFlag: showWireguard,
    setSiteInfo,
    setPatchObject,
    setChangesMade: setSiteInfoChangesMade,
  });

  const selectedWireguardName =
    wireGuardOptions.find((option) => option.id === wireguardId)?.name ?? null;

  const [noteText, setNoteText] = React.useState(siteInfo.notes ?? "");

  // This tab is created once as an element (setActiveTab), so its siteInfo
  // prop does not follow edits. The connection fields' visible_when rules
  // (ENG-2535) need the edited values, so keep a live copy that every VMS
  // field edit updates alongside the page's siteInfo.
  const [liveSiteInfo, setLiveSiteInfo] = React.useState<SiteInfo>(siteInfo);
  React.useEffect(() => {
    setLiveSiteInfo(siteInfo);
  }, [siteInfo]);
  const setSiteInfoAndLive = React.useCallback(
    (update: React.SetStateAction<SiteInfo>) => {
      setSiteInfo(update);
      setLiveSiteInfo(update);
    },
    [setSiteInfo]
  );

  const groupOptions: GroupOption[] = (
    safeGetItem<GroupOption[]>("parentGroups", []) ?? []
  ).filter((group: GroupOption) => group.parent_account === true);

  const groupOptionsIds: Map<number, string> = new Map();
  const groupOptionsNames: Map<string, number> = new Map();
  const groupStandardNames: Map<string, string> = new Map();

  groupOptions.forEach((option: GroupOption) => {
    groupOptionsIds.set(option.id, `${option.name} (group id: ${option.id})`);
  });

  groupOptions.forEach((option: GroupOption) => {
    groupOptionsNames.set(`${option.name} (group id: ${option.id})`, option.id);
  });

  groupOptions.forEach((option: GroupOption) => {
    groupStandardNames.set(
      `${option.name} (group id: ${option.id})`,
      option.name
    );
  });

  const alarmOptions = safeGetItem<AlarmOption[]>("alarmOptions", []) ?? [];

  const alarmIds = new Map();
  alarmOptions.forEach((object: AlarmOption) => {
    alarmIds.set(object.value, object.name);
  });

  const alarmNames = new Map();
  alarmOptions.forEach((object: AlarmOption) => {
    alarmNames.set(object.name, object.value);
  });

  const noVMS =
    siteInfo.integration_type_name === "rtsp" &&
    siteInfo.onboarding_integration === 1;

  const autopatrolSite = siteInfo.integration_type_name === "autopatrol";

  const titlesArray = [
    "Site Name",
    "Timezone",
    ...(autopatrolSite ? [] : ["Autoadd"]),
    "Integration Type",
    ...(autopatrolSite ? [] : ["Alarm Type"]),
    "Parent Group",
  ];

  if (siteInfo.show_ecs_nat_instance) {
    titlesArray.push("Whitelisting");
  }

  const [showPassword, setShowPassword] = React.useState<any>({});

  const handleClickShowPassword = (inputString: string) =>
    setShowPassword((previousState: any) => ({
      ...previousState,
      [inputString]: !previousState[inputString],
    }));

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  React.useEffect(() => {
    vmsVariables.forEach((array: string[]) => {
      if (array[3] === "password") {
        setShowPassword((show: any) => ({ ...show, [array[2]]: false }));
      }
    });
  }, []);

  const renderVMSTable = () => {
    if (noVMS) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={6} align="left" sx={{ fontFamily: "Mulish" }}>
              <Typography variant="body1" sx={{ fontFamily: "Mulish" }}>
                {
                  "To view connection details for specific cameras, find the camera below, then > Show More > Edit Camera > Camera Information > Connection."
                }
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    const cellsPerRow = 6;
    // ENG-2535: fields whose visible_when rule fails against the edited
    // values are not shown; they keep their values and are never patched.
    // Each entry keeps its vmsVariables index for vmsBuilder's write-back.
    const shownEntries = visibleVmsVariables(vmsVariables, liveSiteInfo);
    const totalRows = Math.ceil(shownEntries.length / cellsPerRow);
    const rows = [];

    for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
      const startIndex = rowIndex * cellsPerRow;
      const endIndex = Math.min(startIndex + cellsPerRow, shownEntries.length);
      const rowEntries = shownEntries.slice(startIndex, endIndex);
      const rowVariables = rowEntries.map(({ array }) => array);
      const emptyCells = cellsPerRow - rowVariables.length;

      rows.push(
        <React.Fragment key={`vms-row-${rowIndex}`}>
          <TableHead>
            <TableRow>
              {rowVariables.map((array: string[]) => (
                <TableCell
                  key={array[0]}
                  align="left"
                  sx={{
                    width: "16.66%",
                    fontFamily: "Mulish",
                    fontWeight: "Bold",
                  }}
                >
                  {getVmsDisplayLabel(array[0], siteInfo.integration_type_name)}
                </TableCell>
              ))}
              {emptyCells > 0 && <TableCell colSpan={emptyCells}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key={`${siteInfo.id}-vms-data-row-${rowIndex}`}>
              {rowEntries.map(({ array, index: vmsIndex }) => (
                <TableCell
                  key={array[0]}
                  align="left"
                  sx={{ width: "16.66%", fontFamily: "Mulish" }}
                >
                  {vmsBuilder(
                    array,
                    vmsVariables,
                    setVmsVariables,
                    setPatchObject,
                    setSiteInfoAndLive,
                    liveSiteInfo,
                    setSiteInfoChangesMade,
                    showPassword,
                    handleClickShowPassword,
                    handleMouseDownPassword,
                    handleMouseUpPassword,
                    vmsIndex,
                    userPermissions
                  )}
                </TableCell>
              ))}
              {emptyCells > 0 && <TableCell colSpan={emptyCells}></TableCell>}
            </TableRow>
          </TableBody>
        </React.Fragment>
      );
    }

    return rows;
  };

  return (
    <div className="pb-[10px]">
      <NatWireguardConflictDialog {...conflictDialogProps} />
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 1000, fontFamily: "Mulish" }}
          aria-label="simple table"
        >
          <TableHeadComponent titlesArray={titlesArray} />
          <TableBody>
            <TableRow key={siteInfo.id}>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                <div key={siteInfo.name}>
                  <TextField
                    defaultValue={siteInfo.name}
                    variant="standard"
                    sx={{ width: 200 }}
                    type="string"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setPatchObject((previousState: PatchObject) => {
                        return { ...previousState, name: event.target.value };
                      });
                      setSiteInfo((previousState: SiteInfo) => {
                        return { ...previousState, name: event.target.value };
                      });
                      setSiteInfoChangesMade(true);
                    }}
                  />
                </div>
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                <div key={siteInfo.timezone}>
                  <Autocomplete
                    disablePortal
                    options={safeGetItem<string[]>("timeZoneArray", []) ?? []}
                    defaultValue={siteInfo.timezone}
                    sx={{ width: 200 }}
                    onInputChange={(event, newInputValue) => {
                      setPatchObject((previousState: PatchObject) => {
                        return { ...previousState, timezone: newInputValue };
                      });
                      setSiteInfo((previousState: SiteInfo) => {
                        return { ...previousState, timezone: newInputValue };
                      });
                      setSiteInfoChangesMade(true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        autoComplete="off"
                      />
                    )}
                  />
                </div>
              </TableCell>
              {!autopatrolSite && (
                <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                  {siteInfo.auto_add ? (
                    userPermissions.super_user ? (
                      <Tooltip title={siteInfo.auto_add} placement="top">
                        {checkedCircleIcon}
                      </Tooltip>
                    ) : (
                      checkedCircleIcon
                    )
                  ) : (
                    cancelIconRed
                  )}
                </TableCell>
              )}
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {integrationsMatcher("Name", siteInfo.integration_type)}
              </TableCell>
              {!autopatrolSite && (
                <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                  <div key={siteInfo.default_alarm}>
                    <Autocomplete
                      disablePortal
                      options={alarmOptions.map((option: any) => {
                        return option.name;
                      })}
                      defaultValue={alarmIds.get(siteInfo.default_alarm)}
                      sx={{ width: 150 }}
                      onInputChange={(event, newInputValue) => {
                        setPatchObject((previousState: PatchObject) => {
                          return {
                            ...previousState,
                            default_alarm: alarmNames.get(newInputValue),
                          };
                        });
                        setSiteInfo((previousState: SiteInfo) => {
                          return {
                            ...previousState,
                            default_alarm: alarmNames.get(newInputValue),
                          };
                        });
                        setSiteInfoChangesMade(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          autoComplete="off"
                        />
                      )}
                    />
                  </div>
                </TableCell>
              )}
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                <div key={siteInfo.breadcrumbs[0]}>
                  <Autocomplete
                    disablePortal
                    options={groupOptions.map((option: GroupOption) => {
                      return `${option.name} (group id: ${option.id})`;
                    })}
                    defaultValue={
                      siteInfo.breadcrumbs[siteInfo.breadcrumbs.length - 2]
                    }
                    sx={{ width: 150 }}
                    onInputChange={(event, newInputValue) => {
                      setPatchObject((previousState: PatchObject) => {
                        return {
                          ...previousState,
                          parent_group: {
                            parent: groupOptionsNames.get(newInputValue),
                          },
                        };
                      });
                      setSiteInfo((previousState: SiteInfo) => {
                        return {
                          ...previousState,
                          parent_group: {
                            name: groupStandardNames.get(newInputValue),
                          },
                        };
                      });
                      localStorage.setItem("groupNameChange", "true");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        autoComplete="off"
                      />
                    )}
                  />
                </div>
              </TableCell>
              {siteInfo.show_ecs_nat_instance && (
                <TableCell>
                  <Switch
                    checked={ecsNatInstance}
                    disabled={false}
                    color="warning"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleNatToggle(event.target.checked);
                    }}
                  />
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
        <Table
          sx={{ minWidth: 1000, fontFamily: "Mulish" }}
          aria-label="simple table"
        >
          <TableHeadComponent
            titlesArray={
              shouldShowWireguardAvailabilityZone
                ? ["WireGuard Availability Zone", "Notes", "", "", "", ""]
                : ["Notes", "", "", "", "", ""]
            }
          />
          <TableBody>
            <TableRow key={siteInfo.id}>
              {shouldShowWireguardAvailabilityZone && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", verticalAlign: "top" }}
                >
                  <Autocomplete
                    key={wireguardAutocompleteKey}
                    disablePortal
                    options={wireGuardOptions.map((option) => option.name)}
                    value={selectedWireguardName}
                    sx={{ width: 300 }}
                    onChange={(_event, newValue) => {
                      if (!newValue) {
                        return;
                      }
                      const selectedOption = wireGuardOptions.find(
                        (option) => option.name === newValue
                      );
                      if (selectedOption) {
                        handleWireguardSelection(selectedOption.id);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        autoComplete="off"
                      />
                    )}
                  />
                </TableCell>
              )}
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                <TextField
                  onChange={(event) => {
                    setPatchObject((previousState: PatchObject) => {
                      return { ...previousState, notes: event.target.value };
                    });
                    setSiteInfo((previousState: SiteInfo) => {
                      return { ...previousState, notes: event.target.value };
                    });
                    setNoteText(event.target.value);
                    setSiteInfoChangesMade(true);
                  }}
                  value={noteText}
                  variant="outlined"
                  multiline
                  rows={6}
                  sx={{ fontFamily: "Mulish", width: 400 }}
                />
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              {!shouldShowWireguardAvailabilityZone && (
                <TableCell></TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
            mt: 2,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                colSpan={6}
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                VMS
              </TableCell>
            </TableRow>
          </TableHead>
          {renderVMSTable()}
        </Table>
      </TableContainer>
    </div>
  );
};
